import {useCallback, useEffect} from 'react';
import {useQueryClient} from '@tanstack/react-query';
import {useTranslation} from 'react-i18next';
import {useNavigate} from 'react-router';

import {buildMigrationsToRun} from '@/components/document/model-version/build-migrations-to-run';
import {useConfig} from '@/contexts/config';
import {ModelMigration} from '@/engine/types/model/migration';
import {useUpdateDocument} from '@/hooks/queries/documents';
import {SupportedLocale} from '@/initializers/i18next';
import {Toaster} from '@/initializers/toaster';
import {FullDocument} from '@/interfaces/document';
import {omitMetaFields} from '@/utils/document/serializer';
import ContainerCard from '../container-card';
import Spinner from '../spinner';

const MIN_MIGRATION_DELAY_IN_MS = 2000;

type Props = {
  document: FullDocument;
  targetVersion?: string;
};

function runMigrations(original: FullDocument, migrations: ModelMigration[]) {
  return migrations.reduce(
    (document, {steps}) => {
      return steps.reduce((doc, step) => step.apply(doc), {...document});
    },
    {...original},
  );
}

export function DocumentMigrationHelper({document, targetVersion}: Props) {
  const {t, i18n} = useTranslation('common');
  const {model} = useConfig();
  const effectiveTargetVersion = targetVersion ?? model.version;
  const queryClient = useQueryClient();
  const updateDocument = useUpdateDocument({preventRefetch: true});
  const navigate = useNavigate();

  const refetchDocument = useCallback(
    async (documentId: string) => {
      queryClient.invalidateQueries({queryKey: ['document', documentId]});
    },
    [queryClient],
  );

  const updateMigratedDocument = useCallback(
    async (documentId: string, document: FullDocument) => {
      try {
        const updatedDocument = omitMetaFields(document);
        await updateDocument.mutateAsync({documentId, updatedDocument});
        return true;
      } catch {
        return false;
      }
    },
    [updateDocument],
  );

  useEffect(() => {
    let isRefetchCancelled = false;

    (async () => {
      const migrationsToRunResult = buildMigrationsToRun(
        model.migrations,
        document.modelVersion,
        effectiveTargetVersion,
      );

      if (!migrationsToRunResult.success) {
        Toaster.error(t('components.document.migration.error'));
        navigate('/', {replace: true});
        return;
      }

      const {migrations: migrationsToRun, finalVersion: stampedVersion} = migrationsToRunResult;
      const migratedDocument = runMigrations(document, migrationsToRun);

      const [result] = await Promise.all([
        updateMigratedDocument(document.id, {...migratedDocument, modelVersion: stampedVersion}),
        new Promise(resolve => setTimeout(resolve, MIN_MIGRATION_DELAY_IN_MS)),
      ]);

      if (isRefetchCancelled) {
        return;
      }

      if (result) {
        await refetchDocument(document.id);

        const hasMessage = migrationsToRun.some(m => {
          if (m.message) {
            Toaster.success(m.message[i18n.language as SupportedLocale]);
            return true;
          }
          return false;
        });

        if (!hasMessage) {
          Toaster.success(t('components.document.migration.success'));
        }
      } else {
        Toaster.error(t('components.document.migration.error'));
        navigate('/', {replace: true});
      }
    })();

    return () => {
      isRefetchCancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- should run once
  }, []);

  return (
    <>
      <ContainerCard>
        <ContainerCard.Header>
          <h1 className="text-center text-xl font-bold">{t('components.document.migration.title')}</h1>
        </ContainerCard.Header>
        <ContainerCard.Content className="flex flex-col gap-10">
          <p className="text-center">{t('components.document.migration.description')}</p>
          <div className="flex items-center justify-center">
            <Spinner />
          </div>
        </ContainerCard.Content>
      </ContainerCard>
    </>
  );
}
